import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, Coins, Search, X } from "lucide-react";
import { cn } from "@/cn";
import { CoMark } from "@/components/co-mark";
import { SideNav } from "@/components/side-nav";
import { ThemeToggle } from "@/components/theme-toggle";
import { instrumentSerif } from "@/lib/fonts";
import { getPeopleUsage } from "@/api/people";
import { useAuth } from "@/hooks/useAuth";
import { useMobileNav } from "@/components/mobile-nav-context";

// Mobile navigation (< md): slim top bar + right slide-over drawer.
// Drawer opens from the bottom-tab "More" control.
const STYLE = `
.co-mnav{position:sticky;top:0;z-index:30;background:var(--bg);padding-top:calc(env(safe-area-inset-top) + .8rem)}
.co-mscrim{position:fixed;inset:0;z-index:60;background:rgba(8,8,12,.45);-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);opacity:0;pointer-events:none;transition:opacity .3s ease}
.co-mscrim.open{opacity:1;pointer-events:auto}
.co-mdrawer{position:fixed;top:0;right:0;bottom:0;z-index:61;width:min(20rem,86vw);display:flex;flex-direction:column;overflow-y:auto;overscroll-behavior:contain;transform:translateX(102%);transition:transform .34s cubic-bezier(.32,.72,0,1);will-change:transform;box-shadow:-16px 0 48px -16px rgba(0,0,0,.4);padding-top:calc(env(safe-area-inset-top) + .25rem);padding-bottom:calc(3.5rem + env(safe-area-inset-bottom))}
.co-mdrawer.open{transform:translateX(0)}
.co-msafe{padding-bottom:calc(1rem + env(safe-area-inset-bottom))}
.co-pulse{animation:co-pulse 1.6s ease-in-out infinite}
@keyframes co-pulse{0%,100%{opacity:1}50%{opacity:.35}}
@media(prefers-reduced-motion:reduce){.co-mdrawer,.co-mscrim{transition:none}.co-pulse{animation:none}}
`;

export function MobileNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { signOut } = useAuth();
  const { drawerOpen: open, setDrawerOpen: setOpen, closeDrawer } = useMobileNav();
  const panelRef = useRef<HTMLElement>(null);

  const { data: usage } = useQuery({
    queryKey: ["people-usage"],
    queryFn: getPeopleUsage,
    staleTime: 60_000,
    retry: false,
  });
  const creditsLabel = (() => {
    if (!usage?.connected) return "—";
    const n = usage.emailCredits ?? usage.exportCredits ?? usage.mobileCredits;
    if (n == null) return usage.label ?? "—";
    return n.toLocaleString();
  })();

  // Close on route change.
  useEffect(() => {
    closeDrawer();
  }, [pathname, closeDrawer]);

  // Scroll-lock + Escape + move focus into the drawer while open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const f = panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (f.length === 0) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    const t = window.setTimeout(
      () => panelRef.current?.querySelector<HTMLElement>("a, button")?.focus(),
      60,
    );
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
  }, [open, setOpen]);

  // Swipe-right-to-close.
  const drag = useRef({ x0: 0, dx: 0, active: false });
  const onTouchStart = (e: React.TouchEvent) => {
    drag.current = { x0: e.touches[0].clientX, dx: 0, active: true };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!drag.current.active || !panelRef.current) return;
    const dx = Math.max(0, e.touches[0].clientX - drag.current.x0);
    drag.current.dx = dx;
    panelRef.current.style.transition = "none";
    panelRef.current.style.transform = `translateX(${dx}px)`;
  };
  const onTouchEnd = () => {
    if (!panelRef.current) return;
    panelRef.current.style.transition = "";
    panelRef.current.style.transform = "";
    if (drag.current.dx > 90) setOpen(false);
    drag.current.active = false;
  };

  return (
    <>
      <style>{STYLE}</style>

      <header className="co-mnav border-b border-border px-4 pb-3 md:hidden">
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="flex items-center gap-2"
            aria-label="sales-ops home"
          >
            <CoMark size={26} />
            <span
              className={`${instrumentSerif.className} relative -top-px text-xl text-landing`}
            >
              sales-ops
            </span>
          </Link>
          <div className="ml-auto flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => navigate("/people")}
              aria-label="Search people"
              className="inline-flex min-h-[44px] min-w-[40px] items-center justify-center rounded-md p-2 text-muted hover:bg-surface-hover hover:text-foreground"
            >
              <Search className="size-4" />
            </button>
            <Link
              to="/settings/notifications"
              className="inline-flex min-h-[44px] min-w-[40px] items-center justify-center rounded-md p-2 text-muted hover:bg-surface-hover hover:text-foreground"
              aria-label="Notifications"
            >
              <Bell className="size-4" />
            </Link>
          </div>
        </div>
      </header>

      <div
        className={cn("co-mscrim md:hidden", open && "open")}
        onClick={() => setOpen(false)}
        aria-hidden
      />

      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        inert={!open}
        className={cn(
          "co-mdrawer border-l border-border bg-surface md:hidden",
          open && "open",
        )}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <span className={`${instrumentSerif.className} text-lg text-landing`}>
            Menu
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md p-1.5 text-muted transition-colors hover:bg-surface-hover hover:text-foreground"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="px-3">
          <SideNav mobile onNavigate={() => setOpen(false)} />
        </div>

        <div className="co-msafe mt-auto space-y-3 border-t border-border px-4 pt-4">
          <Link
            to="/settings/credits"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-surface-hover"
          >
            <Coins className="size-4 text-muted-foreground" />
            <span>Credits</span>
            <span className="ml-auto tabular-nums text-muted-foreground">
              {creditsLabel}
            </span>
          </Link>
          <div className="space-y-1 text-sm">
            <Link
              to="/settings/account"
              onClick={() => setOpen(false)}
              className="block rounded-md px-2 py-2 hover:bg-surface-hover"
            >
              Profile
            </Link>
            <Link
              to="/settings/integrations"
              onClick={() => setOpen(false)}
              className="block rounded-md px-2 py-2 hover:bg-surface-hover"
            >
              Integrations
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void signOut();
              }}
              className="block w-full rounded-md px-2 py-2 text-left hover:bg-surface-hover"
            >
              Sign out
            </button>
          </div>
          <div className="flex items-center justify-between">
            <span
              className={`${instrumentSerif.className} text-sm text-faint`}
            >
              local-first · v0
            </span>
            <ThemeToggle />
          </div>
        </div>
      </aside>
    </>
  );
}
